import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, CheckCircle, Eye, X, RefreshCw } from 'lucide-react';
import api from '../services/api';

const Alerts = () => {
  const [alerts, setAlerts]     = useState([]);
  const [filter, setFilter]     = useState('abierta'); // abierta | reconocida | resuelta | all
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const load = async () => {
    try {
      const params = filter !== 'all' ? `?estado=${filter}` : '';
      const [r, s] = await Promise.all([
        api.get(`/api/alerts${params}`),
        api.get('/api/alerts/summary'),
      ]);
      setAlerts(r.data || []);
      setSummary(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [filter]);

  const ack = async (id) => { await api.post(`/api/alerts/${id}/acknowledge`); load(); };
  const resolve = async (id) => { await api.post(`/api/alerts/${id}/resolve`); load(); };

  const sevColor = { critica: '#dc2626', alta: '#ea580c', media: '#ca8a04', baja: '#0891b2' };
  const sevBg    = { critica: '#fee2e2', alta: '#ffedd5', media: '#fef3c7', baja: '#cffafe' };

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><AlertTriangle size={26} /> Alertas</h1>
          <p style={S.subtitle}>{alerts.length} alertas en vista actual</p>
        </div>
        <button style={S.refreshBtn} onClick={load}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
        </button>
      </header>

      {summary && (
        <div style={S.kpis}>
          <div style={{ ...S.kpi, borderLeftColor: '#dc2626' }}>
            <div style={S.kpiVal}>{summary.por_severidad.critica || 0}</div>
            <div style={S.kpiLab}>Críticas</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#ea580c' }}>
            <div style={S.kpiVal}>{summary.por_severidad.alta || 0}</div>
            <div style={S.kpiLab}>Altas</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#ca8a04' }}>
            <div style={S.kpiVal}>{summary.por_severidad.media || 0}</div>
            <div style={S.kpiLab}>Medias</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#0891b2' }}>
            <div style={S.kpiVal}>{summary.por_severidad.baja || 0}</div>
            <div style={S.kpiLab}>Bajas</div>
          </div>
          <div style={{ ...S.kpi, borderLeftColor: '#374151' }}>
            <div style={S.kpiVal}>{summary.abiertas_total}</div>
            <div style={S.kpiLab}>Total abiertas</div>
          </div>
        </div>
      )}

      <div style={S.tabs}>
        {['abierta', 'reconocida', 'resuelta', 'all'].map(t => (
          <button key={t} onClick={() => setFilter(t)}
                  style={{ ...S.tab, ...(filter === t ? S.tabActive : {}) }}>
            {t === 'all' ? 'Todas' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.list}>
        {alerts.length === 0 && (
          <div style={S.empty}>
            <CheckCircle size={48} color="#10b981" />
            <p>Sin alertas en este filtro</p>
          </div>
        )}

        {alerts.map(a => (
          <div key={a.id} style={S.alertCard}>
            <div style={{ ...S.severityBar, background: sevColor[a.severidad] || '#6b7280' }} />
            <div style={S.alertContent}>
              <div style={S.alertHeader}>
                <span style={{
                  ...S.sevBadge,
                  background: sevBg[a.severidad] || '#f3f4f6',
                  color: sevColor[a.severidad] || '#374151'
                }}>{a.severidad?.toUpperCase()}</span>
                <span style={S.tipoTag}>{a.tipo}</span>
                <span style={S.timestamp}>
                  {new Date(a.triggered_at).toLocaleString('es-MX')}
                </span>
              </div>

              <div style={S.alertBody}>
                <strong>{a.numero_unidad ? `MDVR ${a.numero_unidad}` : a.device_id}</strong>
                {a.channel != null && <span style={S.canalTag}>Canal {a.channel}</span>}
                <p style={{ margin: '6px 0', color: '#374151' }}>{a.mensaje}</p>
              </div>

              <div style={S.alertFooter}>
                <small style={S.estado}>Estado: <b>{a.estado}</b></small>
                {a.estado === 'abierta' && (
                  <>
                    <button style={S.btnAck} onClick={() => ack(a.id)}>
                      <Eye size={14} /> Reconocer
                    </button>
                    <button style={S.btnRes} onClick={() => resolve(a.id)}>
                      <CheckCircle size={14} /> Resolver
                    </button>
                  </>
                )}
                {a.estado === 'reconocida' && (
                  <button style={S.btnRes} onClick={() => resolve(a.id)}>
                    <CheckCircle size={14} /> Resolver
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px',
                background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },

  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 },
  kpi: { background: 'white', padding: 14, borderRadius: 8, borderLeft: '4px solid',
         boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  kpiVal: { fontSize: 22, fontWeight: 700 },
  kpiLab: { fontSize: 12, color: '#6b7280', marginTop: 4 },

  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb',
         background: 'white', cursor: 'pointer', fontSize: 14 },
  tabActive: { background: '#3b82f6', color: 'white', borderColor: '#3b82f6' },

  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  alertCard: { background: 'white', borderRadius: 8, display: 'flex',
               overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  severityBar: { width: 5 },
  alertContent: { flex: 1, padding: 14 },
  alertHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  sevBadge: { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 },
  tipoTag: { fontSize: 12, color: '#6b7280', fontFamily: 'monospace',
             background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 },
  timestamp: { fontSize: 12, color: '#9ca3af', marginLeft: 'auto' },
  alertBody: { fontSize: 14 },
  canalTag: { marginLeft: 8, fontSize: 12, background: '#fef3c7',
              color: '#92400e', padding: '2px 8px', borderRadius: 4 },
  alertFooter: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  estado: { color: '#6b7280', flex: 1 },
  btnAck: { padding: '6px 12px', background: '#fef3c7', color: '#92400e',
            border: 'none', borderRadius: 6, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 },
  btnRes: { padding: '6px 12px', background: '#d1fae5', color: '#065f46',
            border: 'none', borderRadius: 6, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 },

  empty: { textAlign: 'center', padding: 60, color: '#6b7280',
           display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
};

export default Alerts;
